'use client';

// IMPORTANT: Import global.css to ensure design system styles are available
// This is required because global-error renders outside the root layout
import './global.css';

import * as Sentry from '@sentry/nextjs';
import { Inter, JetBrains_Mono } from 'next/font/google';
import { NextIntlClientProvider, useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { ErrorContent } from '@/components/Error';
import { Button } from '@/components/ui/button';
// Import fallback translations for the error page
// This is necessary because global-error renders outside the root layout
import fallbackMessages from '@/i18n/locales/en/translation.json';

const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-sans',
});

const jetBrainsMono = JetBrains_Mono({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-mono',
});

type GlobalErrorContentProps = {
  error: Error & { digest?: string };
  onReset: () => void;
};

function GlobalErrorContentWrapper({
  error,
  onReset,
}: GlobalErrorContentProps) {
  const translate = useTranslations();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <ErrorContent
      title={translate('globalErrorTitle')}
      description={error.message || translate('globalErrorDescription')}
      showHomeButton={true}
      // Fatal render error: force a full reload rather than SPA navigation,
      // so the app remounts from a clean state instead of carrying corrupt
      // React state into the home route.
      onHomeClick={() => {
        window.location.href = '/';
      }}
      actions={(
        <Button
          onClick={() => {
            onReset();
          }}
          variant="outline"
          startIcon="lucide:refresh-cw"
        >
          {translate('tryAgain')}
        </Button>
      )}
    />
  );
}

/**
 * Global error boundary component.
 *
 * IMPORTANT: This component renders OUTSIDE the root layout, meaning it does not
 * have access to the NextIntlClientProvider from the root layout. We must provide
 * our own translation context here using fallback English translations.
 *
 * This component must include its own <html> and <body> tags as it completely
 * replaces the root layout when an error occurs. It must also import global.css
 * and set up fonts independently since the root layout styles are not available.
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error#global-error
 */
export default function GlobalError({
  error,
  reset: onReset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html lang="en" className={`${inter.variable} ${jetBrainsMono.variable}`}>
      <body className="font-sans bg-background text-foreground">
        <NextIntlClientProvider locale="en" messages={fallbackMessages}>
          {/* global-error renders in a bare body with no root layout, so it
              owns its own full-viewport centering (PageError does this for
              the in-layout error pages). */}
          <div className="grid min-h-screen place-items-center px-4">
            <GlobalErrorContentWrapper error={error} onReset={onReset} />
          </div>
        </NextIntlClientProvider>
      </body>
    </html>
  );
}
